import { Star } from "lucide-react";
import { SectionHead, Reveal } from "./Reveal";
import { dayName, fmtRange, GOLD } from "../../lib/content";
import { useLang } from "../../lib/i18n";

const INK = "#14110A";

const SpecialtyCard = ({ cls, i, onOpen }) => {
  const { t, lang, pick } = useLang();
  return (
    <Reveal delay={i * 0.07} className="h-full">
      <button onClick={() => onOpen(cls)} data-testid={`specialty-card-${cls.id}`}
        className="group flex h-full w-full flex-col overflow-hidden rounded-2xl border border-[#D4AF37]/50 bg-white text-start shadow-[0_20px_50px_-30px_rgba(60,45,10,0.45)] transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-[#D4AF37]">
        <div className="relative aspect-[3/4] overflow-hidden bg-[#EDE6D6]">
          {cls.image && <img src={cls.image} loading="lazy" decoding="async" alt={t.withName(pick(cls, "style"), pick(cls, "title"))} className="h-full w-full object-cover object-[50%_12%] transition-transform duration-700 group-hover:scale-105" />}
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/45 to-transparent" />
          <span className="absolute start-3 top-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-[#14110A]" style={{ background: GOLD }} data-testid={`specialty-badge-${cls.id}`}>
            <Star size={10} fill={INK} strokeWidth={0} /> {t.specBadge}
          </span>
          <span className="absolute bottom-3 start-3 rounded-full bg-white/90 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.14em] text-[#14110A] backdrop-blur">{pick(cls, "style")}</span>
        </div>
        <div className="flex flex-1 flex-col p-5">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#8A6D1E]">{pick(cls, "tag")}</span>
          <h3 className="mt-1 font-display text-3xl font-bold uppercase tracking-tight text-[#14110A]">{pick(cls, "title")}</h3>
          {cls.ages && (
            <span className="mt-2 inline-flex w-fit items-center rounded-full border border-[#D4AF37] px-2.5 py-0.5 font-mono text-[12px] text-[#6B5418]" data-testid={`specialty-ages-${cls.id}`}>
              {t.ageGroup}: {pick(cls, "ages")}
            </span>
          )}
          <div className="mt-4 flex-1 space-y-1.5">
            {cls.sessions.length ? cls.sessions.map((s, k) => (
              <p key={k} className="flex justify-between gap-2 font-mono text-[13px] text-[#2B261B]">
                <span>{dayName(s.day, lang)}</span><span className={s.start ? "font-medium text-[#8A6D1E]" : "text-[#6E6656]"}>{fmtRange(s, lang)}</span>
              </p>
            )) : <p className="font-mono text-[13px] text-[#6E6656]">{t.scheduleTba}</p>}
          </div>
          <div className="mt-5 flex items-center justify-between border-t border-[#D4AF37]/30 pt-4">
            <span className="text-[12px] uppercase tracking-[0.18em] text-[#6E6656]">{t.monthlyFee}</span>
            <span className="font-display text-lg font-bold text-[#14110A]" data-testid={`specialty-fee-${cls.id}`}>{pick(cls, "fee") || t.contactUs}</span>
          </div>
        </div>
      </button>
    </Reveal>
  );
};

export const Specialty = ({ classes, onOpen }) => {
  const list = classes.filter((c) => c.program === "specialty");
  const { t } = useLang();
  return (
    <section id="specialty" className="relative bg-[#F5F0E6]" data-testid="specialty-section">
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
      <div className="mx-auto max-w-[1440px] px-5 py-24 lg:px-10 lg:py-32">
        <SectionHead light eyebrow={t.specEyebrow} title={<>{t.specTitle[0]}<br />{t.specTitle[1]}</>}>
          {t.specText}
        </SectionHead>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {list.map((c, i) => <SpecialtyCard key={c.id} cls={c} i={i} onOpen={onOpen} />)}
        </div>
      </div>
    </section>
  );
};
